import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Badge, 
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSearch,
  faBell,
  faBellSlash,
  faTrash,
  faMapMarkerAlt,
  faHome,
  faDollarSign,
  faCalendarAlt,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import './SavedSearchesPanel.css';

interface SavedSearch {
  id: string;
  search_name: string;
  search_criteria: any;
  alert_frequency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SavedSearchesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onApplySearch: (criteria: any, searchName: string) => void;
}

const SavedSearchesPanel: React.FC<SavedSearchesPanelProps> = ({ 
  isOpen, 
  onClose, 
  user,
  onApplySearch 
}) => {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSearchId, setActiveSearchId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadSavedSearches();
    }
  }, [isOpen, user]);

  const loadSavedSearches = async () => {
    try {
      const authToken = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      if (!authToken) {
        console.error('No auth token found');
        return;
      }

      console.log('Loading saved searches with token:', authToken ? 'Token exists' : 'No token');
      const response = await axiosInstance.get('/mls-portal/saved-searches', {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      console.log('Saved searches response:', response.data);
      if (response.data.success) {
        setSavedSearches(response.data.data || []);
        console.log('Set saved searches:', response.data.data?.length || 0, 'items');
      }
    } catch (error) {
      console.error('Error loading saved searches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplySearch = (search: SavedSearch) => {
    setActiveSearchId(search.id);
    onApplySearch(search.search_criteria, search.search_name);
  };

  const toggleAlerts = async (searchId: string, currentStatus: boolean) => {
    try {
      const authToken = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      await axiosInstance.put(
        `/mls-portal/saved-searches/${searchId}/alerts`,
        { enabled: !currentStatus },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      // Reload searches to show updated status
      loadSavedSearches();
    } catch (error) {
      console.error('Error toggling alerts:', error);
    }
  };

  const deleteSearch = async (searchId: string) => {
    if (!window.confirm('Are you sure you want to delete this saved search?')) return;
    
    try {
      const authToken = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      await axiosInstance.delete(`/mls-portal/saved-searches/${searchId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      // Reload searches
      loadSavedSearches();
    } catch (error) {
      console.error('Error deleting search:', error);
    }
  };

  const formatCriteria = (criteria: any) => {
    const parts = [];
    
    if (criteria.city) parts.push(`📍 ${criteria.city}`);
    if (criteria.propertyType) parts.push(`🏠 ${criteria.propertyType}`);
    if (criteria.bedrooms) parts.push(`🛏️ ${criteria.bedrooms}+ beds`);
    if (criteria.bathrooms) parts.push(`🚿 ${criteria.bathrooms}+ baths`);
    if (criteria.minPrice || criteria.maxPrice) {
      const min = criteria.minPrice ? `$${(criteria.minPrice/1000).toFixed(0)}k` : '0';
      const max = criteria.maxPrice ? `$${(criteria.maxPrice/1000).toFixed(0)}k` : '∞';
      parts.push(`💰 ${min} - ${max}`);
    }
    if (criteria.minSqft) parts.push(`📐 ${criteria.minSqft}+ sqft`);
    
    return parts;
  };

  return (
    <div className={`saved-searches-panel ${isOpen ? 'open' : ''}`}>
      <div className="panel-header">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faSearch} className="me-2" />
          Saved Searches
        </h5>
        <Button 
          variant="link" 
          className="close-btn p-0" 
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </Button>
      </div>

      <div className="panel-body">
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading your searches...</p>
          </div>
        ) : savedSearches.length === 0 ? (
          <Alert variant="info" className="m-3">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            No saved searches yet. Save your first search from the map view!
          </Alert>
        ) : (
          <div className="searches-list">
            {savedSearches.map((search) => (
              <Card 
                key={search.id} 
                className={`search-card mb-3 ${activeSearchId === search.id ? 'active' : ''}`}
              >
                <Card.Body>
                  <div className="search-header mb-2">
                    <h6 className="search-title mb-1">{search.search_name}</h6>
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {new Date(search.created_at).toLocaleDateString()}
                    </small>
                  </div>

                  <div className="search-criteria mb-3">
                    {formatCriteria(search.search_criteria).map((item, index) => (
                      <div key={index} className="criteria-item">
                        {item}
                      </div>
                    ))}
                  </div>

                  <div className="search-actions">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleApplySearch(search)}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faSearch} className="me-1" />
                      Apply Search
                    </Button>

                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          {search.alert_frequency === 'daily' ? 'Disable alerts' : 'Enable alerts'}
                        </Tooltip>
                      }
                    >
                      <Button
                        size="sm"
                        variant={search.alert_frequency === 'daily' ? 'success' : 'secondary'}
                        onClick={() => toggleAlerts(search.id, search.alert_frequency === 'daily')}
                        className="me-2"
                      >
                        <FontAwesomeIcon 
                          icon={search.alert_frequency === 'daily' ? faBell : faBellSlash} 
                        />
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Delete search</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => deleteSearch(search.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </div>

      {!loading && savedSearches.length > 0 && (
        <div className="panel-footer">
          <small className="text-muted">
            {savedSearches.filter(s => s.alert_frequency === 'daily').length} of {savedSearches.length} searches have alerts enabled
          </small>
        </div>
      )}
    </div>
  );
};

export default SavedSearchesPanel;